// The meeting canvas between Stop and the finished meeting (PL-30).
//
// The operator: "while stopping a meeting recording … some clean state that
// it's now being processed … while being able to see the live transcript until
// it's fully ready and then the canvas switches". One view covers the whole
// wait, so the title never moves and the text never disappears:
//
//   eyebrow     "Finishing up", where "Still recording" was and "Meeting · …" will be
//   status      what is happening now; one muted line with a typical duration
//               (and, for a long wait, that the canvas can be closed); four
//               steps (transcript, speakers, corrections, notes); one quiet action
//   transcript  a browser recording's live rows, de-emphasised, as the latest
//               lines (the words being read at Stop stay in view) with the rest
//               one disclosure away; once the transcript is saved and the notes
//               are being written, the saved rows. An import has no live text.
//
// The capture view renders it right after Stop (this device or another), the
// processing slot renders it while the server transcribes, and the dispatcher
// renders it while a just-finished meeting writes its first notes. When the
// finished view replaces it, useFinishingArrival opens the transcript where
// the person was reading and fades the new content in. The model is
// meeting-finishing-model.ts; copy is ns/meetings-finishing.ts; styles are
// meeting-finishing.css (imported by workspace.css).
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { CheckIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsFinishingCopy, { type MeetingsFinishingCopyKey } from '@/i18n/ns/meetings-finishing';
import meetingsLiveCopy from '@/i18n/ns/meetings-live';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import recordingSafetyCopy from '@/i18n/ns/meeting-recording-safety';
import { useWorkspace } from './context';
import { useMeetingLiveRows } from './meeting-live';
import { formatClock } from './meeting-record-parts';
import { RecorderError } from './meeting-record-control';
import { recorderBacklogSeconds, useMeetingRecorder } from './meeting-recorder';
import {
  activeFinishingStep,
  FINISHING_STEPS,
  finishingEstimate,
  finishingPhase,
  finishingPosition,
  finishingStartMs,
  finishingSteps,
  finishingTail,
  finishingTypicalMs,
  LONG_WAIT_MS,
  nearestSegmentIndex,
  savedTranscriptLines,
  serverTimeMs,
  type FinishingEstimate,
  type FinishingLine,
  type FinishingPhase,
  type FinishingStep,
  type FinishingStepState,
} from './meeting-finishing-model';
import type { MeetingViewSlots } from './meeting-slots';
import { emptyTurnContext, type CompanyMeeting } from './types';

type Copy = Readonly<Record<MeetingsFinishingCopyKey, string>>;

const STEP_LABEL: Record<FinishingStep, MeetingsFinishingCopyKey> = {
  transcript: 'stepTranscript',
  speakers: 'stepSpeakers',
  corrections: 'stepCorrections',
  notes: 'stepNotes',
};
const STATE_LABEL: Record<FinishingStepState, MeetingsFinishingCopyKey> = {
  done: 'stepDone',
  active: 'stepActive',
  waiting: 'stepWaiting',
};
const ACTIVITY: Record<FinishingStep, MeetingsFinishingCopyKey> = {
  transcript: 'activityTranscript',
  speakers: 'activityVoices',
  corrections: 'activityCorrections',
  notes: 'activityNotes',
};
const TICK_MS = 15_000;

// Per-meeting memory shared by the views that take turns rendering the wait
// (capture view, processing slot, dispatcher), each of which mounts afresh:
//   watchedSince  when this tab first saw the meeting finishing; only used when
//                 the server gives no start time at all
//   stopSeen      the Stop time read while capturing or sealing, which later
//                 reads no longer carry
//   expanded      the person asked for the whole transcript
const watchedSince = new Map<string, number>();
const stopSeen = new Map<string, number>();
const expanded = new Set<string>();

/** The clock, re-read every 15 s while mounted. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** How long the wait has run, from the server's clock where it says. */
function elapsedMs(meeting: CompanyMeeting, phase: FinishingPhase, nowMs: number) {
  const stop = serverTimeMs(meeting.capture?.stopRequestedAt);
  if (Number.isFinite(stop)) stopSeen.set(meeting.id, stop);
  const start = finishingStartMs(meeting, phase, stopSeen.get(meeting.id));
  if (!watchedSince.has(meeting.id)) watchedSince.set(meeting.id, nowMs);
  return Math.max(0, nowMs - (Number.isFinite(start) ? start : watchedSince.get(meeting.id)!));
}

// Meetings this device stopped. Stop is final, so the canvas stays on the
// finishing view for the poll where this device's recorder is done but the
// server's Stop is not read back yet. Recording the meeting here clears it.
const stoppedHere = new Set<string>();

/**
 * Whether Stop was pressed for the meeting, here or on another device, while
 * the server may still say capturing. The capture view and the finishing view
 * share it.
 */
export function useMeetingStopped(meeting: CompanyMeeting) {
  const recorderMeeting = useMeetingRecorder((state) => state.meetingId);
  const phase = useMeetingRecorder((state) => state.phase);
  const here = recorderMeeting === meeting.id;
  const stoppingHere = here && (phase === 'stopping' || phase === 'saving');
  const recordingHere = here && !stoppingHere;
  useEffect(() => {
    if (stoppingHere) stoppedHere.add(meeting.id);
    else if (recordingHere) stoppedHere.delete(meeting.id);
  }, [stoppingHere, recordingHere, meeting.id]);
  const stopped =
    stoppingHere ||
    Boolean(meeting.capture?.stopRequestedAt) ||
    (!recordingHere && stoppedHere.has(meeting.id));
  return { here, stopped, saving: here && phase === 'saving' };
}

/** What is happening now, in one line. */
function ActivityLine({
  meeting,
  phase,
  active,
  saving,
  copy,
}: {
  meeting: CompanyMeeting;
  phase: FinishingPhase;
  active: FinishingStep | null;
  saving: boolean;
  copy: Copy;
}) {
  const record = useNamespaceCopy(meetingsRecordCopy);
  const safety = useNamespaceCopy(recordingSafetyCopy);
  const backlog = useMeetingRecorder(recorderBacklogSeconds);
  const online = useMeetingRecorder((state) => state.upload.online);
  const text =
    phase !== 'saving'
      ? copy[ACTIVITY[active ?? 'notes']]
      : !saving
        ? record.sealing
        : online
          ? backlog === null
            ? safety.incomplete
            : formatCopy(record.saving, { seconds: backlog })
          : record.savingOffline;
  return (
    <p className="ws-finishing-activity" data-stage={meeting.stage} role="status">
      {text}
    </p>
  );
}

/** One muted line: the typical duration, then (for a long wait) that it can be closed. */
function MetaLine({
  estimate,
  note,
  copy,
}: {
  estimate: FinishingEstimate | null;
  note: string | null;
  copy: Copy;
}) {
  const typical =
    estimate?.key === 'estimateMinutes'
      ? formatCopy(copy.estimateMinutes, { minutes: estimate.minutes })
      : estimate
        ? copy[estimate.key]
        : null;
  const text = [typical, note].filter(Boolean).join(' ');
  if (!text) return null;
  return (
    <p className="ws-finishing-meta" data-estimate={estimate?.key}>
      {text}
    </p>
  );
}

/**
 * Four bars: done is full, the running step is a partial fill that moves,
 * waiting is the empty track. Labels sit under the bars on a wide canvas; a
 * narrow canvas shows one line naming the running step instead (the list keeps
 * every label for assistive technology).
 */
function Steps({ steps, copy }: { steps: Record<FinishingStep, FinishingStepState>; copy: Copy }) {
  const current = activeFinishingStep(steps);
  const position = finishingPosition(steps);
  return (
    <>
      <ol className="ws-finishing-steps" aria-label={copy.stepsLabel}>
        {FINISHING_STEPS.map((step) => (
          <li
            key={step}
            data-step={step}
            data-state={steps[step]}
            aria-current={step === current ? 'step' : undefined}
          >
            <span className="ws-finishing-bar" aria-hidden="true" />
            <span className="ws-finishing-step-label">
              {steps[step] === 'done' && <CheckIcon size={12} strokeWidth={1.75} aria-hidden />}
              {copy[STEP_LABEL[step]]}
            </span>
            <span className="ws-sr-only">{copy[STATE_LABEL[steps[step]]]}</span>
          </li>
        ))}
      </ol>
      {position && (
        <p className="ws-finishing-step-now" aria-hidden="true">
          {formatCopy(copy.stepPosition, {
            step: copy[STEP_LABEL[position.step]],
            position: position.position,
            total: FINISHING_STEPS.length,
          })}
        </p>
      )}
    </>
  );
}

/** Keep asking Chat about the meeting while it finishes (PT-008), one quiet step away. */
function AskAction({ meeting }: { meeting: CompanyMeeting }) {
  const ws = useWorkspace();
  const live = useNamespaceCopy(meetingsLiveCopy);
  if (ws.turnContext?.meetingIds?.includes(meeting.id)) return null;
  const ask = () =>
    ws.send('', { ...emptyTurnContext(), meetingIds: [meeting.id] }, false, {
      kind: 'meeting',
      id: meeting.id,
    });
  return (
    <button type="button" className="ws-text-button ws-finishing-action" onClick={ask}>
      {live.askAboutMeeting}
    </button>
  );
}

/**
 * The one quiet action: while the notes are written, open the finished meeting
 * without waiting; before that, ask Chat about it.
 */
function FinishingAction({
  meeting,
  phase,
  onShowTranscript,
  copy,
}: {
  meeting: CompanyMeeting;
  phase: FinishingPhase;
  onShowTranscript?: () => void;
  copy: Copy;
}) {
  if (phase !== 'settling') return <AskAction meeting={meeting} />;
  if (!onShowTranscript) return null;
  const open = () => {
    focusOnArrival.add(meeting.id);
    onShowTranscript();
  };
  return (
    <button type="button" className="ws-text-button ws-finishing-action" onClick={open}>
      {copy.showNow}
    </button>
  );
}

/** The estimate, and whether the wait is long enough to say the canvas can be closed. */
function useFinishingEstimate(meeting: CompanyMeeting, phase: FinishingPhase) {
  const now = useNow();
  const typical = finishingTypicalMs(meeting, phase);
  const estimate = finishingEstimate(typical, elapsedMs(meeting, phase, now));
  const longWait = (typical ?? 0) >= LONG_WAIT_MS || estimate?.key === 'estimateLonger';
  // Inbox tells the creator when the transcript is ready, so the note is only
  // true before it is saved.
  return { estimate, longWait: longWait && phase !== 'settling' };
}

/**
 * The finishing view. `onShowTranscript` (the dispatcher's, while the first
 * notes are written) offers the finished view without waiting.
 */
export function MeetingFinishingView({
  meeting,
  onShowTranscript,
  memberNotes,
  navigation,
  showTranscript = true,
  panel,
}: {
  meeting: CompanyMeeting;

  onShowTranscript?: () => void;
} & MeetingViewSlots) {
  const copy = useNamespaceCopy(meetingsFinishingCopy);
  const ws = useWorkspace();
  const { here, stopped, saving } = useMeetingStopped(meeting);
  const phase =
    finishingPhase(meeting, { stopping: stopped, nowMs: Date.now() }) ??
    (meeting.status === 'ready' ? 'settling' : 'saving');
  const steps = finishingSteps(meeting, phase);
  const { estimate, longWait } = useFinishingEstimate(meeting, phase);
  const creator = Boolean(meeting.createdBy && meeting.createdBy === ws.user?.id);
  return (
    <div className="ws-canvas-body ws-meeting-finishing" data-phase={phase}>
      <div className="ws-eyebrow ws-finishing-eyebrow">{copy.eyebrow}</div>
      <h1>{meeting.title}</h1>
      {navigation}
      <div {...panel}>
        <section className="ws-finishing-status">
          <ActivityLine
            meeting={meeting}
            phase={phase}
            active={activeFinishingStep(steps)}
            saving={saving}
            copy={copy}
          />
          <MetaLine
            estimate={estimate}
            note={creator && longWait ? copy.leaveNote : null}
            copy={copy}
          />
          <Steps steps={steps} copy={copy} />
          {here && meeting.canEdit !== false && <RecorderError />}
          <FinishingAction
            meeting={meeting}
            phase={phase}
            onShowTranscript={onShowTranscript}
            copy={copy}
          />
        </section>
        {memberNotes}
        {showTranscript && (
          <FinishingTranscript meeting={meeting} phase={phase} here={here} copy={copy} />
        )}
      </div>
    </div>
  );
}

/**
 * The text under the header: the saved rows while the notes are written; a
 * browser recording's live rows before that. An import was never transcribed
 * live, so it has none (and reads no live rows).
 */
function FinishingTranscript({
  meeting,
  phase,
  here,
  copy,
}: {
  meeting: CompanyMeeting;
  phase: FinishingPhase;
  here: boolean;
  copy: Copy;
}) {
  // Keyed by meeting: rows, cursor and disclosure never cross meetings.
  if (phase === 'settling')
    return (
      <Lines
        key={meeting.id}
        meetingId={meeting.id}
        label={copy.transcript}
        lines={savedTranscriptLines(meeting)}
        tail
      />
    );
  if (meeting.captureMethod !== 'browser') return null;
  return (
    <LiveLines
      key={meeting.id}
      meeting={meeting}
      polling={phase === 'saving'}
      here={here}
      copy={copy}
    />
  );
}

/**
 * The live rows, de-emphasised. Right after Stop this device still holds its
 * own preview lines, which may be ahead of what the server has stored: the
 * longer of the two is shown (both use the same session:segment keys).
 */
function LiveLines({
  meeting,
  polling,
  here,
  copy,
}: {
  meeting: CompanyMeeting;
  polling: boolean;
  here: boolean;
  copy: Copy;
}) {
  const { rows, loaded } = useMeetingLiveRows(meeting.id, polling);
  const local = useMeetingRecorder((state) => state.preview.lines);
  const server: FinishingLine[] = rows.map((row) => ({
    key: `${row.previewSessionId}:${row.seg}`,
    atMs: row.atMs,
    text: row.text,
  }));
  const lines = here && local.length > server.length ? local : server;
  if (!loaded && !lines.length) return <LinesSkeleton label={copy.liveTranscript} />;
  return (
    <Lines
      meetingId={meeting.id}
      label={copy.liveTranscript}
      lines={lines}
      empty={copy.noLiveText}
      tail
    />
  );
}

function LinesSkeleton({ label }: { label: string }) {
  return (
    <section className="ws-finishing-transcript" aria-label={label} aria-busy="true">
      <div className="ws-finishing-head">
        <h2 className="ws-finishing-label">{label}</h2>
      </div>
      <div className="ws-finishing-skeleton" aria-hidden="true">
        <span className="ws-skeleton" />
        <span className="ws-skeleton" />
        <span className="ws-skeleton" />
      </div>
    </section>
  );
}

// ── Reading position ───────────────────────────────────────────────────────

/**
 * Where the person was reading in each meeting's finishing transcript: kept
 * only once the transcript has scrolled past the top of the canvas (someone
 * looking at the header stays at the top of the finished view).
 */
type Anchor = { atMs: number; offset: number };
const anchors = new Map<string, Anchor>();
const BREATHING_PX = 8;

/** The nearest scrolling ancestor (the canvas), or null. */
function scrollParent(element: HTMLElement | null): HTMLElement | null {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return node;
  }
  return null;
}

/** Record the first visible line while the person scrolls the canvas. */
function useReadingAnchor(meetingId: string, section: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = scrollParent(section.current);
    if (!container) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const top = container.getBoundingClientRect().top;
      const start = section.current?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
      if (start > top) return void anchors.delete(meetingId);
      const lines = section.current?.querySelectorAll<HTMLElement>('[data-at]') ?? [];
      for (const line of lines) {
        // A line the narrow canvas trims (display: none) measures 0 × 0 and is skipped.
        const box = line.getBoundingClientRect();
        if (box.bottom <= top) continue;
        anchors.set(meetingId, { atMs: Number(line.dataset.at), offset: box.top - top });
        return;
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [meetingId, section]);
}

/**
 * The lines. With `tail`, the latest lines under a quiet "Show all lines (N)"
 * disclosure, which stays open for the meeting once used and moves focus to
 * the first line it reveals. The stylesheet trims the tail further, and shows
 * the disclosure for a shorter transcript, on a narrow canvas.
 */
function Lines({
  meetingId,
  label,
  lines,
  empty,
  tail = false,
}: {
  meetingId: string;
  label: string;
  lines: FinishingLine[];
  empty?: string;
  tail?: boolean;
}) {
  const copy = useNamespaceCopy(meetingsFinishingCopy);
  const section = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(() => !tail || expanded.has(meetingId));
  const revealed = useRef(false);
  useReadingAnchor(meetingId, section);
  useLayoutEffect(() => {
    if (!open || !revealed.current) return;
    revealed.current = false;
    section.current?.querySelector<HTMLElement>('.ws-finishing-line')?.focus();
  }, [open]);
  if (!lines.length && !empty) return null;
  const { collapsible, hidden, shown } = finishingTail(lines, open);
  const reveal = () => {
    expanded.add(meetingId);
    revealed.current = true;
    setOpen(true);
  };
  const listId = `ws-finishing-lines-${meetingId}`;
  return (
    <section className="ws-finishing-transcript" aria-label={label} ref={section}>
      <div className="ws-finishing-head">
        <h2 className="ws-finishing-label">{label}</h2>
        {collapsible && (
          <button
            type="button"
            className="ws-text-button ws-finishing-more"
            data-compact-only={hidden ? undefined : 'true'}
            aria-controls={listId}
            aria-expanded={open}
            onClick={reveal}
          >
            {formatCopy(copy.showAllLines, { count: lines.length })}
          </button>
        )}
      </div>
      {lines.length ? (
        <div className="ws-finishing-lines" id={listId} data-tail={collapsible || undefined}>
          {shown.map((line, index) => (
            <p
              key={line.key}
              className="ws-finishing-line"
              data-at={line.atMs}
              tabIndex={index === 0 ? -1 : undefined}
            >
              <span className="ws-finishing-time">{formatClock(line.atMs)}</span>
              <span>{line.text}</span>
            </p>
          ))}
        </div>
      ) : (
        <p className="ws-finishing-empty">{empty}</p>
      )}
    </section>
  );
}

/**
 * What a failed recording heard live (the rows stay until a transcript is
 * saved), under the failure view, so a retry never costs the person the text.
 * Renders nothing when there were no live rows.
 */
export function MeetingFinishingTranscript({ meeting }: { meeting: CompanyMeeting }) {
  return <HeardLive key={meeting.id} meeting={meeting} />;
}

function HeardLive({ meeting }: { meeting: CompanyMeeting }) {
  const copy = useNamespaceCopy(meetingsFinishingCopy);
  const { rows } = useMeetingLiveRows(meeting.id, false);
  if (!rows.length) return null;
  return (
    <div className="ws-canvas-body ws-finishing-after">
      <Lines
        meetingId={meeting.id}
        label={copy.liveTranscript}
        lines={rows.map((row) => ({
          key: `${row.previewSessionId}:${row.seg}`,
          atMs: row.atMs,
          text: row.text,
        }))}
      />
    </div>
  );
}

// ── Arrival of the finished view ───────────────────────────────────────────

// Meetings whose finished view was asked for from the finishing view's own
// button: that button unmounts, so focus moves to the finished view's title.
const focusOnArrival = new Set<string>();

/** Fade the finished view in once; the marker goes when the fade ends. */
function markArrived(body: HTMLElement) {
  body.dataset.arrived = 'true';
  const done = (event: AnimationEvent) => {
    if (!event.animationName.startsWith('ws-finishing-')) return;
    delete body.dataset.arrived;
    body.removeEventListener('animationend', done);
  };
  body.addEventListener('animationend', done);
}

/** Open the transcript at the row nearest the line being read, if any. */
function restoreReading(meeting: CompanyMeeting) {
  const anchor = anchors.get(meeting.id);
  anchors.delete(meeting.id);
  const index = anchor ? nearestSegmentIndex(meeting, anchor.atMs) : null;
  const row = index === null ? null : document.getElementById(`meeting-segment-${index}`);
  const container = scrollParent(row);
  if (!anchor || !row || !container) return;
  const offset = row.getBoundingClientRect().top - container.getBoundingClientRect().top;
  container.scrollTop += offset - Math.max(anchor.offset, BREATHING_PX);
}

/**
 * Called by the dispatcher on every render with whether the finishing view is
 * showing. On the render where the finished view replaces it, the transcript
 * opens at the row nearest the line the person was reading (or stays at the
 * top) and `body` fades in once. An ordinary open of a finished meeting does
 * nothing, and the fade marker never outlives the meeting it was for.
 */
export function useFinishingArrival(
  meeting: CompanyMeeting,
  finishing: boolean,
  body: RefObject<HTMLElement | null>,
) {
  const was = useRef<string | null>(null);
  const marked = useRef<{ id: string; element: HTMLElement } | null>(null);
  useLayoutEffect(() => {
    // The canvas element outlives a meeting: its marker must not replay the
    // fade for the next meeting's content.
    if (marked.current && marked.current.id !== meeting.id) {
      delete marked.current.element.dataset.arrived;
      marked.current = null;
    }
    if (finishing) {
      was.current = meeting.id;
      return;
    }
    if (was.current !== meeting.id || !body.current) return;
    was.current = null;
    markArrived(body.current);
    marked.current = { id: meeting.id, element: body.current };
    restoreReading(meeting);
    if (focusOnArrival.delete(meeting.id))
      body.current.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true });
  }, [finishing, meeting, body]);
}
